import React, { useState, useEffect } from 'react';
import { getAdminUserDetails } from '../../Api';
import { XMarkIcon, PencilIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';

const AdminUserDetails = ({ adminUserId, onBack, onEdit }) => {
  const [adminUser, setAdminUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAdminUserDetails();
  }, [adminUserId]);

  const fetchAdminUserDetails = async () => {
    try {
      if (!adminUserId) {
        setError('Admin user ID is required');
        setLoading(false);
        return;
      }
      
      setLoading(true);
      const response = await getAdminUserDetails(adminUserId);
      setAdminUser(response || {});
      setError(null);
    } catch (err) {
      setError('Failed to load admin user details');
      console.error('Error fetching admin user details:', err);
      console.error('Error response data:', err.response?.data);
      console.error('Error response status:', err.response?.status);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <p className="text-red-700">{error}</p>
        <button 
          onClick={fetchAdminUserDetails}
          className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Admin user not found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <div className="flex items-center">
          <button
            onClick={onBack}
            className="mr-4 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Admin User Details
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              View and manage admin user information
            </p>
          </div>
        </div>

      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Personal Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Personal Information</h3>
              
              <div className="space-y-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0 h-16 w-16">
                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center">
                      <span className="text-blue-600 font-bold text-lg">
                        {adminUser.firstName.charAt(0)}
                        {adminUser.lastName.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="text-lg font-medium text-gray-900">
                      {adminUser.firstName} {adminUser.lastName}
                    </h4>
                    <p className="text-sm text-gray-500">Admin User</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Full Name</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {adminUser.firstName} {adminUser.lastName}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Email</label>
                    <p className="mt-1 text-sm text-gray-900">{adminUser.email}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Role</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      adminUser.roleName.toLowerCase() === 'super_admin' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {adminUser.roleName.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      adminUser.status 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {adminUser.status ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Account Information */}
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Account Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">User ID</label>
                  <p className="mt-1 text-sm text-gray-900 font-mono">{adminUser.adminId}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Created At</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(adminUser.createdAt).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Updated</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {new Date(adminUser.updatedAt).toLocaleString()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Last Login</label>
                  <p className="mt-1 text-sm text-gray-900">
                    {adminUser.lastLoginAt 
                      ? new Date(adminUser.lastLoginAt).toLocaleString() 
                      : 'Never logged in'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Permissions</label>
                  <div className="mt-2 space-y-2">
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      {adminUser.roleName.toLowerCase() === 'super_admin' ? 'Full Access' : 'Limited Access'}
                    </span>
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      User Management
                    </span>
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      Content Management
                    </span>
                    <span className="inline-block px-2 py-1 text-xs bg-gray-100 text-gray-800 rounded">
                      Analytics Access
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Audit Trail Section */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h3>
              
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-md p-3">
                  <p className="text-sm font-medium text-gray-900">Account Created</p>
                  <p className="text-xs text-gray-500">
                    {new Date(adminUser.createdAt).toLocaleString()}
                  </p>
                </div>
                
                {adminUser.lastLoginAt && (
                  <div className="bg-gray-50 rounded-md p-3">
                    <p className="text-sm font-medium text-gray-900">Last Login</p>
                    <p className="text-xs text-gray-500">
                      {new Date(adminUser.lastLoginAt).toLocaleString()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetails;